/**
 * LocationStep Component
 *
 * Step 2 of listing wizard: location selection.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ListingFormData, ListingLocation } from '../../hooks';
import { useTaxonomies } from '../../hooks';

interface LocationStepProps {
  data: ListingFormData;
  onChange: (updates: Partial<ListingFormData>) => void;
  onLocationChange: (updates: Partial<ListingLocation>) => void;
}

export const LocationStep: React.FC<LocationStepProps> = ({
  data,
  onLocationChange,
}) => {
  const { data: taxonomies, isLoading } = useTaxonomies();
  const [showMap, setShowMap] = useState(false);

  const filteredAreas = taxonomies?.areas.filter(
    (a) => a.parentSlug === data.location.state
  ) || [];

  const filteredCities = taxonomies?.cities.filter(
    (c) => c.parentSlug === data.location.area
  ) || [];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Where's your property located?
        </h3>
        <p className="text-gray-600 mb-6">
          Your address is only shared with guests after they book.
        </p>
      </div>

      {/* Country (Fixed) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Country
        </label>
        <input
          type="text"
          value="Lebanon"
          disabled
          className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-gray-500"
        />
      </div>

      {/* Governorate */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Governorate <span className="text-red-500">*</span>
        </label>
        {isLoading ? (
          <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
        ) : (
          <select
            value={data.location.state}
            onChange={(e) =>
              onLocationChange({ state: e.target.value, area: '', city: '' })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select governorate</option>
            {taxonomies?.states.map((state) => (
              <option key={state.slug} value={state.slug}>
                {state.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Area */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Area / District <span className="text-red-500">*</span>
        </label>
        <select
          value={data.location.area}
          onChange={(e) => onLocationChange({ area: e.target.value, city: '' })}
          disabled={!data.location.state}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">Select area</option>
          {filteredAreas.map((area) => (
            <option key={area.slug} value={area.slug}>
              {area.name}
            </option>
          ))}
        </select>
      </div>

      {/* City */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          City / Town
        </label>
        <select
          value={data.location.city}
          onChange={(e) => onLocationChange({ city: e.target.value })}
          disabled={!data.location.area}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">Select city (optional)</option>
          {filteredCities.map((city) => (
            <option key={city.slug} value={city.slug}>
              {city.name}
            </option>
          ))}
        </select>
      </div>

      {/* Street Address */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Street address
        </label>
        <input
          type="text"
          value={data.location.address}
          onChange={(e) => onLocationChange({ address: e.target.value })}
          placeholder="e.g., 123 Main Street"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <p className="text-sm text-gray-500 mt-1">
          This is private and only shared with confirmed guests.
        </p>
      </div>

      {/* Map Pin */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pin your location on the map
        </label>
        {data.location.coordinates ? (
          <div className="relative rounded-xl overflow-hidden border border-gray-200">
            <MiniMap coords={data.location.coordinates} />
            <button
              type="button"
              onClick={() => setShowMap(true)}
              className="absolute bottom-3 right-3 px-4 py-2 bg-white text-sm font-medium text-gray-700 rounded-lg shadow hover:bg-gray-50 transition-colors"
            >
              Change location
            </button>
          </div>
        ) : (
          <div
            className="h-64 bg-gray-100 rounded-xl flex items-center justify-center cursor-pointer border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors"
            onClick={() => setShowMap(true)}
          >
            <div className="text-center">
              <MapIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">Click to set location on map</p>
              <p className="text-sm text-gray-500 mt-1">
                This helps guests find your property
              </p>
            </div>
          </div>
        )}

        {/* Map Modal with Leaflet */}
        {showMap && (
          <LocationMapModal
            initialCoords={data.location.coordinates}
            area={data.location.area}
            onConfirm={(coords) => {
              onLocationChange({ coordinates: coords });
              setShowMap(false);
            }}
            onClose={() => setShowMap(false)}
          />
        )}
      </div>

      {/* Summary */}
      {data.location.state && (
        <div className="bg-primary-50 rounded-xl p-4">
          <p className="text-sm text-primary-700">
            <strong>Location:</strong>{' '}
            {[
              data.location.city &&
                taxonomies?.cities.find((c) => c.slug === data.location.city)?.name,
              taxonomies?.areas.find((a) => a.slug === data.location.area)?.name,
              taxonomies?.states.find((s) => s.slug === data.location.state)?.name,
              'Lebanon',
            ]
              .filter(Boolean)
              .join(', ')}
          </p>
        </div>
      )}
    </div>
  );
};

// Mini map preview showing the pinned location
const MiniMap: React.FC<{ coords: { lat: number; lng: number } }> = ({ coords }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Destroy previous instance if coords changed
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(containerRef.current, {
      center: [coords.lat, coords.lng],
      zoom: 15,
      scrollWheelZoom: false,
      dragging: false,
      zoomControl: false,
      attributionControl: false,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
      subdomains: 'abcd',
    }).addTo(map);

    const icon = L.divIcon({
      className: 'custom-marker',
      html: `<div style="width:36px;height:36px;background:#10b981;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.3);">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
      </div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 36],
    });

    L.marker([coords.lat, coords.lng], { icon }).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [coords.lat, coords.lng]);

  return <div ref={containerRef} className="h-64 w-full" />;
};

// Leaflet Map Modal
interface LocationMapModalProps {
  initialCoords: { lat: number; lng: number } | null | undefined;
  area: string;
  onConfirm: (coords: { lat: number; lng: number }) => void;
  onClose: () => void;
}

const AREA_DEFAULTS: Record<string, { lat: number; lng: number }> = {
  keserwan: { lat: 33.98, lng: 35.68 },
  batroun: { lat: 34.25, lng: 35.66 },
  bsharri: { lat: 34.25, lng: 36.01 },
  jezzine: { lat: 33.54, lng: 35.58 },
  zahle: { lat: 33.85, lng: 35.9 },
  'mount-lebanon': { lat: 33.85, lng: 35.6 },
  'north-lebanon': { lat: 34.43, lng: 35.83 },
  'south-lebanon': { lat: 33.27, lng: 35.22 },
  beirut: { lat: 33.89, lng: 35.5 },
  bekaa: { lat: 33.85, lng: 35.9 },
};

const LocationMapModal: React.FC<LocationMapModalProps> = ({
  initialCoords,
  area,
  onConfirm,
  onClose,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const defaultCenter = AREA_DEFAULTS[area] || { lat: 33.89, lng: 35.5 };
  const startCoords = initialCoords || defaultCenter;
  const [pin, setPin] = useState(startCoords);

  const updateMarker = useCallback((latlng: { lat: number; lng: number }) => {
    if (!mapRef.current) return;
    if (markerRef.current) {
      markerRef.current.setLatLng([latlng.lat, latlng.lng]);
    } else {
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:40px;height:40px;background:#10b981;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.3);">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </div>`,
        iconSize: [40, 40],
        iconAnchor: [20, 40],
      });
      markerRef.current = L.marker([latlng.lat, latlng.lng], { icon }).addTo(mapRef.current);
    }
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [startCoords.lat, startCoords.lng],
      zoom: initialCoords ? 15 : 12,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 19,
      subdomains: 'abcd',
    }).addTo(map);

    mapRef.current = map;
    updateMarker(startCoords);

    map.on('click', (e: L.LeafletMouseEvent) => {
      const coords = { lat: e.latlng.lat, lng: e.latlng.lng };
      setPin(coords);
      updateMarker(coords);
    });

    // Fix tiles not rendering in modal
    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-gray-900">Set Location</h4>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-3">Click on the map to place your pin.</p>
        <div
          ref={mapContainerRef}
          className="h-96 rounded-xl overflow-hidden mb-2"
        />
        <p className="text-xs text-gray-400 mb-4">
          Pin: {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => onConfirm(pin)}
            className="flex-1 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600"
          >
            Confirm Location
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded-xl font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Icons
const MapIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default LocationStep;
